import { AlertTriangle, Shield, FileText } from 'lucide-react';

export default function NarrativeSection({ narrative, tierColor }) {
  if (!narrative) return null;

  const severityClass = (s) => {
    if (s === 'high') return 'severity-high';
    if (s === 'moderate') return 'severity-moderate';
    return 'severity-low';
  };

  return (
    <div className="space-y-4">

      {/* Summary card — tier-colored left border */}
      {narrative.summary && (
        <div
          className="card border-l-4"
          style={{ borderLeftColor: tierColor }}
        >
          <div className="flex items-start gap-3">
            <FileText className="text-teal flex-shrink-0 mt-1" size={20} />
            <div>
              <h3 className="font-heading text-base font-bold text-navy mb-2">Profile summary</h3>
              <p className="text-sm leading-relaxed text-gray-700">
                {narrative.summary}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Concerns + Mitigants in two-column layout */}
      <div className="grid md:grid-cols-2 gap-4">

        {/* Concerns column */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="text-orange-500" size={18} />
            <h3 className="font-heading text-base font-bold text-navy">
              Concerns ({narrative.concerns?.length || 0})
            </h3>
          </div>
          {narrative.concerns && narrative.concerns.length > 0 ? (
            <ul className="space-y-3">
              {narrative.concerns.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className={severityClass(c.severity)}>{c.severity}</span>
                  <span className="text-gray-700 flex-1">{c.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-400 italic py-2">
              No significant concerns surfaced.
            </div>
          )}
        </div>

        {/* Mitigants column */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="text-teal" size={18} />
            <h3 className="font-heading text-base font-bold text-navy">
              Mitigants ({narrative.mitigants?.length || 0})
            </h3>
          </div>
          {narrative.mitigants && narrative.mitigants.length > 0 ? (
            <ul className="space-y-3">
              {narrative.mitigants.map((m, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-teal mt-0.5 flex-shrink-0">✓</span>
                  <span className="text-gray-700">{m.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-400 italic py-2">
              No counterbalancing mitigants identified at this location.
            </div>
          )}
        </div>
      </div>

      {/* Underwriting note callout */}
      {narrative.underwriting_note && (
        <div className="card bg-light border-teal/30">
          <div className="flex items-start gap-3">
            <div className="text-teal font-heading font-bold text-xs uppercase tracking-widest flex-shrink-0 pt-0.5">
              Underwriting<br/>Note
            </div>
            <div className="flex-1 border-l border-teal/30 pl-4">
              <p className="text-sm italic text-navy leading-relaxed">
                {narrative.underwriting_note}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fallback notice if narrative LLM failed */}
      {narrative.available === false && (
        <div className="text-xs text-gray-400 italic text-center">
          Narrative generation unavailable for this report. Factors and data sources shown below.
        </div>
      )}
    </div>
  );
}
